/*
 * Purpose: Inline vault create/unlock — compose & readiness without leaving flow
 * Last modified: 2026-06-28 | Agent
 * Completeness: 94
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { KeyRound, Lock, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useSecretStore } from '@/stores/secretStore';
import { REMEDIATION } from '@/lib/remediationRoutes';
import { PRODUCT_TERMS } from '@/lib/productTerms';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface VaultUnlockInlineProps {
  className?: string;
  compact?: boolean;
}

export function VaultUnlockInline({ className, compact }: VaultUnlockInlineProps) {
  const hasEncryptedVault = useSecretStore((s) => s.hasEncryptedVault);
  const isUnlocked = useSecretStore((s) => s.isUnlocked);
  const isSaving = useSecretStore((s) => s.isSaving);
  const unlockVault = useSecretStore((s) => s.unlockVault);

  const [passphrase, setPassphrase] = useState('');

  if (isUnlocked) return null;

  const handleUnlock = async () => {
    if (!passphrase.trim()) {
      toast.error('Enter your vault passphrase', {
        action: { label: 'Focus field', onClick: () => document.getElementById('vault-inline-pass')?.focus() },
      });
      return;
    }
    try {
      await unlockVault(passphrase);
      setPassphrase('');
      toast.success('Vault unlocked — keys available on this page');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Wrong passphrase', {
        action: { label: 'Open Setup', onClick: () => { window.location.href = REMEDIATION.setupVault; } },
      });
    }
  };

  if (!hasEncryptedVault) {
    return (
      <div
        className={cn(
          'ab-interactive-alert rounded-md border border-amber-500/40 bg-amber-500/5 p-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3',
          className
        )}
      >
        <div className="flex items-start gap-2 min-w-0 flex-1">
          <Shield className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-xs text-foreground">
            <span className="font-medium">Vault first</span> — create a secrets vault before onboarding Gmail & keys.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <Button size="sm" asChild className="shrink-0">
            <Link to={REMEDIATION.setupVault}>Create vault</Link>
          </Button>
          <Button size="sm" variant="secondary" asChild className="shrink-0">
            <Link to={REMEDIATION.setup}>Open {PRODUCT_TERMS.onboardingWizard}</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'ab-interactive-alert rounded-md border border-amber-500/40 bg-amber-500/5 p-3 space-y-2',
        className
      )}
    >
      <div className="flex items-center gap-2 text-xs font-medium text-foreground">
        <Lock className="h-3.5 w-3.5 text-amber-600" />
        Vault locked — unlock here to use saved keys on this page
      </div>
      <div
        className={cn(
          'ui-field-action-group ui-field-action-group--stack',
          compact && 'sm:items-end',
          'ab-interactive-field rounded-md'
        )}
      >
        <div className="flex-1 min-w-0">
          {!compact && <Label htmlFor="vault-inline-pass" className="text-xs">Vault passphrase</Label>}
          <Input
            id="vault-inline-pass"
            type="password"
            autoComplete="current-password"
            placeholder="Vault passphrase"
            className={cn('text-sm', !compact && 'mt-1')}
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleUnlock();
            }}
            disabled={isSaving}
          />
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <Button
            size="sm"
            className="ui-field-action-btn"
            onClick={() => void handleUnlock()}
            disabled={isSaving}
          >
            <KeyRound className="h-3.5 w-3.5 mr-1" />
            Unlock
          </Button>
          <Button size="sm" variant="secondary" asChild>
            <Link to={REMEDIATION.setup}>Open {PRODUCT_TERMS.onboardingWizard}</Link>
          </Button>
          <Button size="sm" variant="ghost" asChild>
            <Link to={REMEDIATION.builderSecrets}>Manage in Builder</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
